import { isDefined } from '@sgme/fp';
import { unminifyError, unminifyStackTrace } from '@sgme/stacktrace';
import { lifeRingApiConnector } from 'apiConnector/life-ring';
import type { ErrorBoundaryContext } from 'components/common-form/builder/form/errorBoundary';
import { isHttpError } from 'epic/apiErrorEpicHelper';
import type { Thunk } from 'state/state.common';

import { appCrashed } from './appCrash.actions';
import { isApplicationCrashed, isAutomaticEmailSent } from './appCrash.selector';

const TechnicalTag = 'Technical';

export function appCrashedSendGenericHelpRequestThunk(content: string[], tags: string[]): Thunk<void> {
  return (_dispatch, _getState, { httpClient }) => {
    lifeRingApiConnector(httpClient).sendMail({ userMessages: content, tags }).subscribe();
  };
}

export function appCrashedSendHelpRequestThunk(
  errorMessage: string,
  userComment: string,
  isAutomatic: boolean,
): Thunk<void> {
  return (dispatch, _getState) => {
    const content = `The unrecoverable error "${errorMessage}" happened, user provided the following comments: ${userComment}`;
    dispatch(appCrashedSendGenericHelpRequestThunk([content], isAutomatic ? [TechnicalTag] : []));
  };
}

export function errorInEpicAutomaticEmailThunk(message: string, error: Error): Thunk<void> {
  return async (dispatch, getState) => {
    const state = getState();

    const isCrashed = isApplicationCrashed(state);
    const isEmailSent = isAutomaticEmailSent(state);

    const alreadyCrashAndEmailSent = isCrashed && isEmailSent;
    const isDeveloper = window.sgmeConfiguration.isDeveloper ?? false;
    if (alreadyCrashAndEmailSent) {
      return;
    }
    const err = await unminifyError(error);
    let httpErrorMessage;
    if (isHttpError(error)) {
      httpErrorMessage = [
        `Url : ${err.url ?? ''}`,
        `Status : ${err.status ?? ''}`,
        `Correlation ID : ${err.correlationId ?? ''}`,
      ].join(`\n`);
    }
    const errorMessage = error !== undefined ? `\nError:\n${err.name}:${err.message}\n${err.stack ?? ''}` : '';
    const technicalMessage = [message, errorMessage, httpErrorMessage].filter(isDefined).join('\n');
    dispatch(appCrashed(technicalMessage, err, true));
    if (!isDeveloper) {
      dispatch(appCrashedSendGenericHelpRequestThunk([technicalMessage], [TechnicalTag]));
    }
  };
}
type ErrorKey = 'name' | 'message' | 'componentStack' | 'stack';
type UnminifiedError = {
  [k in ErrorKey]?: string | null;
};
function formatWithLabel(message: string | null | undefined, label: string) {
  if (message) {
    return [label, message];
  }
  return [];
}
function formatStackWithLabel(stack: string | null | undefined, label: string) {
  const stackArray = stack?.split('\n') ?? [];
  if (stackArray.length > 0) {
    return [label, ...stackArray];
  }
  return [];
}

export function errorInBoundaryAutomaticEmailThunk(
  error: unknown,
  componentStack: string | null | undefined,
  context?: ErrorBoundaryContext,
): Thunk<void> {
  return async (dispatch, getState) => {
    const state = getState();

    const isCrashed = isApplicationCrashed(state);
    const isEmailSent = isAutomaticEmailSent(state);
    const isDeveloper = window.sgmeConfiguration.isDeveloper ?? false;

    const alreadyCrashAndEmailSent = isCrashed && isEmailSent;
    if (alreadyCrashAndEmailSent) {
      return;
    }
    const err = (await unminifyError(error)) as unknown as UnminifiedError;

    err.componentStack = await unminifyStackTrace(componentStack);

    const errorMessages =
      err === undefined
        ? []
        : [
            'Technical error',
            ...formatWithLabel(err.name, 'Name :'),
            ...formatWithLabel(err.message, 'Message :'),
            ...formatStackWithLabel(err.componentStack, 'Component stack :'),
            ...formatStackWithLabel(err.stack, 'Stack :'),
          ];
    const contextDetails = context !== undefined ? Object.entries(context).map(([k, v]) => `${k}:${v}`) : [];
    const contextMessages = contextDetails.length > 0 ? ['Context :', ...contextDetails] : [];

    const messages = [
      `An error was catched by errorBoundary:`,
      ...contextMessages,
      ...contextDetails,
      ...errorMessages,
    ];
    dispatch(appCrashed(messages.join('\n'), err, true));

    if (!isDeveloper) {
      dispatch(appCrashedSendGenericHelpRequestThunk(messages, [TechnicalTag]));
    }
  };
}
